/**
 * @jsxRuntime classic
 * @jsx jsx
 * @jsxFrag
 */
import React from 'react';

import { defineMessages, useIntl } from 'react-intl-next';

import { cssMap, jsx } from '@atlaskit/css';
import Heading from '@atlaskit/heading';
import { StaggeredEntrance } from '@atlaskit/motion';
import { Box, Flex, Stack } from '@atlaskit/primitives/compiled';
import { token } from '@atlaskit/tokens';

import { SuggestedEmptyView } from './SuggestedEmptyView';
import { SuggestedErrorView } from './SuggestedErrorView';
import { SuggestedItem, type SuggestedItemData } from './SuggestedItem';

const messages = defineMessages({
	suggested: {
		id: 'ai-audio.playlist-suggested.header',
		defaultMessage: 'Suggested',
		description: 'Header for the suggested items section in the playlist',
	},
} as const);

const styles = cssMap({
	playlist: {
		flex: 1,
		paddingTop: token('space.150'),
		paddingRight: token('space.150'),
		paddingBottom: token('space.150'),
		paddingLeft: token('space.150'),
	},
	sectionHeader: {
		textAlign: 'left',
		paddingLeft: token('space.100'),
		paddingBottom: token('space.100'),
		paddingRight: token('space.050'),
		color: token('color.text.subtle'),
	},
	sectionList: {
		listStyleType: 'none',
		margin: '0',
		padding: '0',
	},
});

interface SuggestedSectionContentProps {
	suggestedItems?: SuggestedItemData[];
	onRetry?: () => void;
}

function SuggestedSectionContent({
	suggestedItems,
	onRetry,
}: SuggestedSectionContentProps): React.ReactNode {
	if (!suggestedItems) {
		return <SuggestedErrorView onRetry={onRetry} />;
	}

	if (suggestedItems.length < 1) {
		return <SuggestedEmptyView />;
	}

	return (
		<Stack as="ul" space="space.100" xcss={styles.sectionList}>
			{suggestedItems.map((item) => (
				<li key={item.id}>
					<SuggestedItem data={item} />
				</li>
			))}
		</Stack>
	);
}

interface SuggestedViewProps {
	suggestedItems?: SuggestedItemData[];
	onRetry?: () => void;
}

export const SuggestedView = ({ suggestedItems, onRetry }: SuggestedViewProps) => {
	const { formatMessage } = useIntl();

	return (
		<StaggeredEntrance columns={1}>
			<Flex xcss={styles.playlist} direction="column">
				<Box xcss={styles.sectionHeader}>
					<Heading as="h4" size="xxsmall">
						{formatMessage(messages.suggested)}
					</Heading>
				</Box>
				<SuggestedSectionContent suggestedItems={suggestedItems} onRetry={onRetry} />
			</Flex>
		</StaggeredEntrance>
	);
};
